import React, { createContext, useState, useEffect, useContext } from 'react';

import CartEventsContext from '../../common/contexts/CartEventsContext';
import RewardShopContext from '../../common/contexts/RewardShopContext';
import { RewardShopArticle } from '../../common/utils/types';
import { NumberOfProductType, NumberOfPetType, ProductType, PetType } from '../types';

interface FiltersContextProps {
  onSelectProductTypes: (productTypes: string[]) => void;
  onClearFilters: () => void;
  onSetMinValue: (value: number) => void;
  onSetMaxValue: (value: number) => void;
  onSetPreviousPurchased: (value: boolean) => void;
  onSetRedeemableRewards: (value: boolean) => void;
  onSelectPetTypes: (petTypes: string[]) => void;
  onShowFilters: (showFilters: boolean, defaultView?: string) => void;
  onSelectDonationArticles: (selectDonationArticles: boolean) => void;
  defaultView: any;
  anyFilterApplied: boolean;
  articlesFiltered: RewardShopArticle[];
  selectedProductTypes: string[];
  selectedPetTypes: string[];
  maxValue: number;
  minValue: number;
  maxPoints: number;
  previousPurchased: boolean;
  showFilters: boolean;
  ProductTypes: NumberOfProductType[];
  PetTypes: NumberOfPetType[];
  selectDonationArticles: boolean;
  redeemableRewards: boolean;
}

const FiltersContext = createContext<Partial<FiltersContextProps>>({});

export const FiltersProvider: React.FC<any> = ({ children }): JSX.Element => {
  const { standardArticles, redeemableBalance } = useContext(CartEventsContext);
  const { chosenArticleIds } = useContext(RewardShopContext);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [defaultView, setDefaultView] = useState<string | undefined>('all');
  const [filteredArticles, setFilteredArticles] = useState<RewardShopArticle[]>([]);
  const [maxPoints, setMaxPoints] = useState<number>();
  const [ProductTypes, setProductTypes] = useState<NumberOfProductType[]>([]);
  const [PetTypes, setPetTypes] = useState<NumberOfPetType[]>([]);
  const [selectDonationArticles, setSelectDonationArticles] = useState<boolean>(false);
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [selectedPetTypes, setSelectedPetTypes] = useState<string[]>([]);
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>();
  const [previousPurchased, setPreviousPurchased] = useState<boolean>(false);
  const [redeemableRewards, setRedeemableRewards] = useState<boolean>(false);

  useEffect(() => {
    if (standardArticles) {
      setPetTypes(onSetPetTypes());
      setProductTypes(onSetProductTypes(standardArticles));
      const maxPoints: number = Math.max(...standardArticles.map((e) => e.points));
      setMaxPoints(maxPoints);
      setMaxValue(maxPoints);
      setFilteredArticles(standardArticles);
    }
  }, [standardArticles]);

  const onShowFilters = (showFilters: boolean, defaultView?: string): void => {
    setShowFilters(showFilters);
    setDefaultView(defaultView);
  };

  const onSelectDonationArticles = (isDonationArticleFilterSelected: boolean): void => {
    isDonationArticleFilterSelected && sendFilterAdobeEvent('event562');
    setSelectDonationArticles(isDonationArticleFilterSelected);
  };

  const onSelectProductTypes = (productTypes: string[]): void => {
    productTypes.length && sendFilterAdobeEvent('event555');
    setSelectedProductTypes(productTypes);
  };

  const onSelectPetTypes = (petTypes: string[]): void => setSelectedPetTypes(petTypes);

  const onSetMinValue = (value: number): void => setMinValue(value);

  const onSetMaxValue = (value: number): void => setMaxValue(value);

  const onClearFilters = (): void => {
    setSelectedProductTypes([]);
    setSelectedPetTypes([]);
    setMinValue(0);
    setMaxValue(maxPoints);
    setPreviousPurchased(false);
    setSelectDonationArticles(false);
    setRedeemableRewards(false);
    sendFilterAdobeEvent('event554');
  };

  const unique = (array: ProductType[] | PetType[]) => {
    const result: NumberOfPetType[] & NumberOfProductType[] = [];
    const groups: ProductType[] & PetType[] = [];
    array.forEach((e) => {
      if (e !== undefined) {
        const index = groups.findIndex((elem) => elem.title === e.title);
        if (index === -1) {
          groups.push(e);
          result.push({ item: e, numberOfArticles: 1 });
        } else {
          result[index].numberOfArticles++;
        }
      }
    });
    return result;
  };

  const petTypeOrdered = (array: NumberOfPetType[]) => {
    const cat = array.find((e) => e.item.petKey === 'CATS');
    const dog = array.find((e) => e.item.petKey === 'DOGS');
    if (cat) {
      const catIndex = array.indexOf(cat);
      array.splice(catIndex, 1);
      array.unshift(cat);
    }
    if (dog) {
      const dogIndex = array.indexOf(dog);
      array.splice(dogIndex, 1);
      array.unshift(dog);
    }
    return array;
  };

  const flattenDeep = (array: any): any => {
    return array.reduce(
      (acc: any, val: any) => (Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val)),
      [],
    );
  };

  const onSetProductTypes = (articles: RewardShopArticle[]): NumberOfProductType[] => {
    const productTypes: ProductType[] = articles?.map((e) => e.productType!) ?? [];
    return unique(flattenDeep(productTypes)).sort((a, b) =>
      a.item.title.localeCompare(b.item.title),
    );
  };

  const onSetPetTypes = (): NumberOfPetType[] => {
    const petTypes: PetType[] = standardArticles?.map((e) => e.petType!) ?? [];
    return petTypeOrdered(unique(flattenDeep(petTypes)));
  };

  const anyFilterApplied = !(
    !selectedPetTypes.length &&
    !selectedProductTypes.length &&
    !minValue &&
    maxValue === maxPoints &&
    !previousPurchased &&
    !redeemableRewards &&
    !selectDonationArticles
  );

  const onSetPreviousPurchased = (value: boolean): void => {
    // @ts-ignore
    SendAdobeData(
      'clickTrack',
      '^tm:Loyalty^trk:Bonus Points^val:Reward Shop / Filter Previous Rewards ' + value,
    );
    setPreviousPurchased(value);
  };

  const onSetRedeemableRewards = (value: boolean): void => {
    // @ts-ignore
    SendAdobeData(
      'customClickTrack',
      'Loyalty',
      'Bonus Points',
      'Reward Shop / Filter Only Redeemable Rewards ' + value,
    );
    setRedeemableRewards(value);
  };

  const onFilterByPoints = (article: RewardShopArticle): boolean => {
    return (
      article.points >= minValue && (isNaN(maxValue ?? 0) || article.points <= (maxValue ?? 0))
    );
  };

  const onFilterByPetTypes = (article: RewardShopArticle): boolean => {
    let correctPetTypes = true;
    if (selectedPetTypes.length > 0) {
      correctPetTypes =
        article.petType !== undefined ? selectedPetTypes.includes(article.petType.title) : false;
    }
    return correctPetTypes;
  };

  const onFilterByProductTypes = (article: RewardShopArticle): boolean => {
    let correctProductTypes = true;
    if (selectedProductTypes.length > 0) {
      correctProductTypes =
        article.productType !== undefined
          ? selectedProductTypes.includes(article.productType.title)
          : false;
    }
    return correctProductTypes;
  };

  const onFilterByPreviousPurchased = (article: RewardShopArticle): boolean => {
    let correctPreviousPurchased = true;
    if (previousPurchased) {
      correctPreviousPurchased = (chosenArticleIds?.indexOf(article.articleId) ?? -1) > -1;
    }
    return correctPreviousPurchased;
  };

  const onFilterByDonationArticles = (article: RewardShopArticle): boolean => {
    return selectDonationArticles ? article.articleType === 'Donation' : true;
  };

  const onFilterByRedeemableRewards = (article: RewardShopArticle): boolean => {
    return redeemableRewards ? article.points <= (redeemableBalance ?? 0) : true;
  };

  const onArticlesFiltered = () => {
    return standardArticles === null
      ? []
      : standardArticles?.filter((article) => {
          const correctPetTypes = onFilterByPetTypes(article);
          const correctProductTypes = onFilterByProductTypes(article);
          const correctPoints = onFilterByPoints(article);
          const correctPreviousPurchased = onFilterByPreviousPurchased(article);
          const correctDonationArticle = onFilterByDonationArticles(article);
          const correctRedeemableRewards = onFilterByRedeemableRewards(article);
          return (
            correctProductTypes &&
            correctPetTypes &&
            correctPoints &&
            correctPreviousPurchased &&
            correctDonationArticle &&
            correctRedeemableRewards
          );
        });
  };

  const onFilterWithoutProducts = () => {
    return standardArticles === null
      ? []
      : standardArticles?.filter((article) => {
          const correctPetTypes = onFilterByPetTypes(article);
          const correctPoints = onFilterByPoints(article);
          const correctPreviousPurchased = onFilterByPreviousPurchased(article);
          const correctRedeemableRewards = onFilterByRedeemableRewards(article);
          return (
            correctPetTypes && correctPoints && correctPreviousPurchased && correctRedeemableRewards
          );
        });
  };

  function sendFilterAdobeEvent(event: any) {
    // @ts-ignore
    typeof SendAdobeData === 'function' && SendAdobeData('bpFilter', event);
  }

  useEffect(() => {
    const articlesFilteredWithoutProducts = onFilterWithoutProducts();
    setProductTypes(onSetProductTypes(articlesFilteredWithoutProducts ?? []));
  }, [selectedPetTypes, minValue, maxValue, previousPurchased, redeemableRewards]);

  useEffect(() => {
    if (selectedProductTypes.length !== 0) {
      const productTypesKeys = ProductTypes.map((ProductType) => {
        return ProductType.item.title;
      });
      const newSelectedProductTypes: string[] = [];
      selectedProductTypes.forEach((selectedProductType) => {
        if (productTypesKeys.includes(selectedProductType)) {
          newSelectedProductTypes.push(selectedProductType);
        }
      });
      setSelectedProductTypes(newSelectedProductTypes);
    }
  }, [ProductTypes]);

  useEffect(() => {
    if (showFilters) {
      sendFilterAdobeEvent('event553');
    }
  }, [showFilters]);

  useEffect(() => {
    const filteredArticles = onArticlesFiltered();
    if (filteredArticles?.length !== standardArticles?.length && selectedPetTypes.length) {
      showFilters ? sendFilterAdobeEvent('event556') : sendFilterAdobeEvent('event559');
    }
    setFilteredArticles(filteredArticles ?? []);
  }, [selectedPetTypes]);

  useEffect(() => {
    const filteredArticles = onArticlesFiltered();
    setFilteredArticles(filteredArticles ?? []);
  }, [selectedProductTypes]);

  useEffect(() => {
    if (standardArticles) {
      const filteredArticles = onArticlesFiltered();
      filteredArticles?.length !== standardArticles.length &&
        (minValue !== 0 || maxValue !== maxPoints) &&
        sendFilterAdobeEvent('event557');
      setFilteredArticles(filteredArticles ?? []);
    }
  }, [minValue, maxValue]);

  useEffect(() => {
    const filteredArticles = onArticlesFiltered();
    setFilteredArticles(filteredArticles ?? []);
  }, [previousPurchased, redeemableRewards, selectDonationArticles]);

  useEffect(() => {
    if (redeemableRewards) {
      const filteredArticles = onArticlesFiltered();
      setFilteredArticles(filteredArticles ?? []);
      const articlesFilteredWithoutProducts = onFilterWithoutProducts();
      setProductTypes(onSetProductTypes(articlesFilteredWithoutProducts ?? []));
    }
  }, [redeemableBalance]);

  return (
    <FiltersContext.Provider
      value={{
        onSelectProductTypes,
        onClearFilters,
        onSetMinValue,
        onSetMaxValue,
        onSelectDonationArticles,
        onSetPreviousPurchased,
        onSelectPetTypes,
        onShowFilters,
        onSetRedeemableRewards,
        defaultView,
        anyFilterApplied,
        articlesFiltered: filteredArticles,
        selectedProductTypes,
        selectedPetTypes,
        maxValue,
        redeemableRewards,
        minValue,
        maxPoints,
        previousPurchased,
        showFilters,
        ProductTypes,
        PetTypes,
        selectDonationArticles,
      }}>
      {children}
    </FiltersContext.Provider>
  );
};
export default FiltersContext;
